import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  currentWorkspace, disconnect, listConnectionActivity, listConnections, startOAuth,
} from "../lib/api";
import { ConnectionActivity } from "../components/ConnectionActivity";
import { InfoTip, TipList, TipP, TipRow } from "../components/InfoTip";
import { ConnectionAccessPanel } from "../components/ConnectionAccessPanel";
import { FacebookIcon, YouTubeIcon } from "../components/PlatformIcons";
// The permission list is read from the one place that holds it, never retyped
// here: this tooltip, the home page, the Security page and the Meta and Google
// submissions must all say the same words, and a copy is how they stop doing
// that. See lib/scopes.ts.
import { GOOGLE_SCOPES, META_SCOPES, type Scope } from "../lib/scopes";
import { logError } from "../lib/logger";
import { limitOf, loadEntitlements, remaining } from "../lib/entitlements";
import { useWorkspace } from "../lib/WorkspaceContext";
import { forgetConnections } from "../lib/useConnections";
// The brand switcher lives in the top bar; this screen is where the list behind
// it is edited, and both read the same store so they cannot disagree (ADR-0029).
import { useBrands, forgetBrands } from "../lib/useBrands";
import { setConnectionBrand } from "../lib/brands";
import { BrandsCard } from "../components/BrandsCard";
import { BrandNotificationsCard } from "../components/BrandNotificationsCard";
import type { Entitlements } from "../lib/types";
// Lending an account is addressed to an organisation, so the picker needs the
// ones this user belongs to (ADR-0022 §3).
import { myOrganisations, type Organisation } from "../lib/organisation";
import { Link } from "react-router-dom";
import { PLATFORM_LABEL, type AuditEvent, type Connection, type Platform } from "../lib/types";

/**
 * `scope` → the one thing it is for, flattened from the same table the buttons
 * read. A scope a network reports that we never asked for has no entry, and is
 * shown without an explanation rather than with a guessed one.
 */
const SCOPE_WHY: Record<string, string> = Object.fromEntries(
  [...META_SCOPES, ...GOOGLE_SCOPES].map(([scope, why]) => [scope, why]),
);

/**
 * The permissions one network is about to be asked for, each beside the single
 * thing it does. Rendered from `scopes.ts` rather than written out here, so the
 * sentence someone reads before pressing Connect is the same sentence in the
 * App Review submission — a reviewer comparing the two must not find them
 * worded differently, and two hand-maintained copies always end up that way.
 */
function ScopeDisclosure({ scopes, lede }: { scopes: readonly Scope[]; lede: string }) {
  return (
    <>
      <TipP>{lede}</TipP>
      <TipList>
        {scopes.map(([scope, why]) => <TipRow key={scope} term={scope} meaning={why} />)}
      </TipList>
      <TipP muted>
        We never post without an action you took, and you can disconnect the account here at
        any time.
      </TipP>
    </>
  );
}

const ACCOUNT_ID_LABEL: Record<Platform, string> = {
  facebook: "Page ID",
  instagram: "Instagram ID",
  youtube: "Channel ID",
};

export function Connections() {
  // Connecting an account creates a workspace-owned credential others publish
  // through, so it is its own permission — as is disconnecting (ADR-0011).
  const { can } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [activity, setActivity] = useState<AuditEvent[]>([]);
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [error, setError] = useState<string | null>(params.get("error"));
  // The third outcome: accounts connected, but not the ones asked for. It is
  // not an error — something worked — and it must not be styled as success
  // either, or "Connected 1 account" goes on hiding a missing Instagram.
  const [notice, setNotice] = useState<string | null>(params.get("notice"));
  const [busy, setBusy] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<Organisation[]>([]);
  const { brands, activeBrandId, activeBrand, selectBrand } = useBrands(workspaceId);

  const connected = params.get("connected");

  async function load() {
    const ws = await currentWorkspace();
    setWorkspaceId(ws);
    // The rail and the top bar draw this same list from a cache, so a returning
    // OAuth redirect has to drop it — otherwise an account just connected is
    // missing from the chrome until the tab is reloaded.
    forgetConnections(ws);
    setConnections(await listConnections(ws));
    // Never let a missing activity log hide the connections themselves.
    setActivity(await listConnectionActivity(ws).catch(() => []));
    setEnt(await loadEntitlements(ws).catch(() => null));
    setOrgs(await myOrganisations().catch(() => []));
  }

  /** After filing an account, or after the brand list itself changed.
   *
   *  Dropping the brand cache is enough to re-read it: every `useBrands` is a
   *  listener on that store, so the switcher in the top bar and this screen
   *  refresh from one invalidation rather than each fetching its own copy. */
  async function reloadAll() {
    forgetBrands(workspaceId ?? undefined);
    if (workspaceId) {
      forgetConnections(workspaceId);
      setConnections(await listConnections(workspaceId));
    }
  }

  useEffect(() => {
    load().catch((e) => {
      setError(String(e.message ?? e));
      void logError("connection_oauth", e, { step: "load_connections" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function connect(platform: Platform) {
    if (!workspaceId) return;
    setBusy(platform);
    setError(null);
    setNotice(null);
    try {
      window.location.href = await startOAuth(platform, workspaceId);
    } catch (e: any) {
      setError(e.message);
      setBusy(null);
      void logError("connection_oauth", e, { step: "start_oauth", workspaceId, platform });
    }
  }

  async function remove(c: Connection) {
    if (!confirm(`Disconnect ${c.display_name}? Scheduled posts to this account will fail.`)) return;
    await disconnect(c.id);
    if (workspaceId) {
      forgetConnections(workspaceId);
      setConnections(await listConnections(workspaceId));
    }
  }

  const all = connections.filter((c) => c.status !== "revoked");
  // The top bar's brand filter decides what this list shows. It is a view
  // filter and nothing more — a hidden account is still connected, still
  // publishes on schedule, and still counts against the plan, which is why the
  // cap below is counted from `all` rather than from what is on screen.
  const active = activeBrandId ? all.filter((c) => c.brand_id === activeBrandId) : all;
  const brandName = (id: string | null) =>
    (id ? brands.find((b) => b.id === id)?.name : null) ?? null;
  const counts = new Map<string, number>();
  for (const c of all) {
    if (c.brand_id) counts.set(c.brand_id, (counts.get(c.brand_id) ?? 0) + 1);
  }
  const unfiled = all.filter((c) => !c.brand_id).length;
  // The cap is enforced by a trigger on insert; this only decides whether the
  // buttons are worth pressing.
  const accountCap = limitOf(ent, "connections");
  const accountsLeft = remaining(ent, "connections");
  const atCap = accountsLeft === 0;
  const mayConnect = can("connection.connect");
  const mayDisconnect = can("connection.disconnect");
  const mayOrganise = can("connection.organise");

  return (
    <>
      <h1>Connections</h1>

      {connected && (
        <div className="card ok">
          Connected {connected} account{connected === "1" ? "" : "s"}.
          <button className="link" onClick={() => setParams({})}>Dismiss</button>
        </div>
      )}
      {error && (
        <div className="card err">
          {error}
          <button className="link" onClick={() => { setError(null); setParams({}); }}>Dismiss</button>
        </div>
      )}
      {notice && (
        <div className="card notice">
          {notice}
          <button className="link" onClick={() => { setNotice(null); setParams({}); }}>Dismiss</button>
        </div>
      )}

      <div className="card">
        <h2>
          Add an account
          <InfoTip label="What connecting an account gives Simino Social">
            <TipP>
              Connecting sends you to Facebook or Google to sign in. They — not us — ask whether
              to grant access, and you choose which Pages or channels to include.
            </TipP>
            <TipP>
              We store the account&rsquo;s id, name and picture, and an access token held
              encrypted and never shown to anyone, including people you invite. We use it to
              publish what you compose, and for nothing else.
            </TipP>
            <TipP muted>
              Full detail on the <Link to="/security">Security</Link> page and in the{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </TipP>
          </InfoTip>
        </h2>
        {/* WORDED FOR THE CURRENT META APP, which is set up with Facebook Login
            for Business. Meta allows one Instagram setup per app — "You can only
            add one setup per app. If you want to implement both setups, create an
            app for each setup" — so Instagram Login needs a second app that does
            not exist, and Instagram is reachable ONLY through a linked Page.
            There is no `Connect Instagram` button, because a control that cannot
            be pressed is a promise, and the second app is not scheduled. Say what
            works instead. The code path is written and dormant — when the app
            exists the button returns. See docs/TODO_INSTAGRAM_ONLY_INTEGRATION.md
            and docs/PRODUCT_BACKLOG.md. */}
        <p className="muted">
          Instagram connects through Facebook: one sign-in picks up the Pages you administer
          together with any Instagram professional account linked to them, and Instagram then
          publishes using that Page's access token.
        </p>

        {/* Both remaining ways to be ineligible are free to fix and take about a
            minute, but only if you know about them BEFORE spending a round trip
            through a consent screen that ends in a refusal. Collapsed, because
            most people connecting an account already meet all of it. */}
        <details className="requirements">
          <summary>What you'll need</summary>
          <ul>
            <li>
              <strong>To publish to Facebook:</strong> a Facebook <em>Page</em> you administer.
              Posting to a personal profile is not something Facebook's API allows any app to
              do. Creating a Page is free.
            </li>
            <li>
              <strong>To publish to Instagram:</strong> a <em>professional</em> account —
              Business or Creator. Instagram allows no app to publish from a personal account.
              Switching is free and reversible, in Instagram under Settings → Account type.
            </li>
            <li>
              <strong>Instagram also needs a Page.</strong> Your professional account
              has to be linked to a Facebook Page you administer — that link is how we find it.
              Add it in the Page&rsquo;s settings under <em>Linked accounts</em>. An Instagram
              account with no Page behind it cannot be connected here at all.
            </li>
          </ul>
        </details>
        <div className="row" style={{ marginTop: ".7rem" }}>
          {/* The disclosure sits beside the button that triggers the consent
              dialog, which is the moment it is worth reading — not a page away
              on Security, and not only in the policy. */}
          <span className="connect-with-tip">
            <button className="connect-btn" onClick={() => connect("facebook")}
                    disabled={!workspaceId || !!busy || atCap || !mayConnect}
                    title="Sign in with Facebook to publish to a Page you administer, and to any Instagram professional account linked to it.">
              <FacebookIcon />
              {busy === "facebook" ? "Redirecting…" : "Connect Facebook "}
            </button>
            <InfoTip label="What Facebook permissions this asks for">
              <ScopeDisclosure
                scopes={META_SCOPES}
                lede="Facebook will ask you to grant these, and only these:"
              />
            </InfoTip>
          </span>
          <span className="connect-with-tip">
            <button className="connect-btn" onClick={() => connect("youtube")}
                    disabled={!workspaceId || !!busy || atCap || !mayConnect}
                    title="Sign in with Google to upload videos to a YouTube channel you choose.">
              <YouTubeIcon />
              {busy === "youtube" ? "Redirecting…" : "Connect YouTube"}
            </button>
            <InfoTip label="What Google permissions this asks for" align="end">
              <ScopeDisclosure
                scopes={GOOGLE_SCOPES}
                lede="Google will ask you to grant these, and only these:"
              />
            </InfoTip>
          </span>
        </div>

        {/* Not a warning and not an error: the capability exists, it is just
            reached from the other button. Phrased as what to do, not as what is
            missing — and with no date attached to what we have not built. */}
        <p className="notice info" style={{ marginTop: ".7rem" }}>
          <strong>Instagram is connected through Facebook.</strong> Choose{" "}
          <em>Connect Facebook</em> and authorise the Page your Instagram professional account
          is linked to — the Instagram account is picked up in the same step and appears in the
          list below, ready to publish to. There is no separate Instagram sign-in: an Instagram
          account that is not linked to a Page cannot be connected.
        </p>

        {!mayConnect && (
          <p className="notice" style={{ marginTop: ".7rem" }}>
            You can publish to the accounts connected here, but connecting a new one is an owner or
            admin action. Nobody publishing on your behalf ever receives the account's credentials.
          </p>
        )}

        {accountCap !== null && (
          atCap ? (
            <p className="notice upgrade" style={{ marginTop: ".7rem" }}>
              You have connected all {accountCap} accounts your plan allows.{" "}
              <Link to="/plans">See plans</Link>
            </p>
          ) : (
            <p className="muted" style={{ marginTop: ".7rem" }}>
              {all.length} of {accountCap} accounts connected on your plan.
            </p>
          )
        )}
      </div>

      {workspaceId && (
        <BrandsCard
          workspaceId={workspaceId}
          brands={brands}
          counts={counts}
          unfiled={unfiled}
          mayOrganise={mayOrganise}
          // Filing a Page and writing the words that go out under its name are
          // two different rights (`connection.organise` and `post.create`).
          // Someone holding only the first can still add a brand; they are not
          // shown boxes whose contents the database would refuse.
          mayWriteDetails={can("post.create")}
          onChanged={reloadAll}
        />
      )}

      {/* Only meaningful once a specific brand is chosen — "all brands" has
          no single notification list to show. */}
      {workspaceId && activeBrand && (
        <BrandNotificationsCard
          workspaceId={workspaceId}
          brandId={activeBrand.id}
          brandName={activeBrand.name}
          mayOrganise={mayOrganise}
        />
      )}

      {/* The filter is set in the top bar, so the screen it acts on has to say
          it is on and offer the way out. Without this, "where did my YouTube
          channel go" has no answer anywhere on this page. */}
      {activeBrand && active.length > 0 && (
        <div className="card notice brand-filter">
          Showing the {all.length === active.length ? "" : `${active.length} of ${all.length} `}
          account{active.length === 1 ? "" : "s"} filed under <strong>{activeBrand.name}</strong>.
          <button className="link" onClick={() => selectBrand(null)}>Show all brands</button>
        </div>
      )}

      {all.length === 0
        ? <div className="card muted">No accounts connected yet — connect one to publish.</div>
        : active.length === 0
        ? (
          <div className="card muted">
            No accounts are filed under {activeBrand?.name ?? "this brand"} yet.{" "}
            <button className="link" onClick={() => selectBrand(null)}>Show all brands</button>
          </div>
        )
        : active.map((c) => (
          <div key={c.id}>
            <div className="card row">
              {c.avatar_url
                ? <img className="avatar" src={c.avatar_url} alt="" />
                : <div className="avatar" />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{c.display_name}</strong>
                <div className="muted">
                  {PLATFORM_LABEL[c.platform]}
                  {c.platform === "instagram" && c.metadata?.linked_page_name
                    ? ` · via ${String(c.metadata.linked_page_name)}`
                    : ""}
                </div>
                {/* What was actually granted, so a connection that looks fine
                    but is missing a scope is visible without reading the DB. */}
                <dl className="conn-detail">
                  <dt>{ACCOUNT_ID_LABEL[c.platform]}</dt>
                  <dd><code>{c.provider_account_id}</code></dd>
                  <dt>
                    Permissions granted
                    {/* Not the same list as the button above: that one is what
                        we ask for, this is what the network says it actually
                        gave. They differ when someone unticks a Page, and the
                        difference is why a publish will fail later. */}
                    <InfoTip label={`What ${c.display_name} granted, and what each permission is for`}>
                      <TipP>
                        What {PLATFORM_LABEL[c.platform]} reports it granted this account —
                        which can be less than we asked for, if a permission was declined.
                      </TipP>
                      {c.scopes?.length ? (
                        <TipList>
                          {c.scopes.map((s) => (
                            <TipRow key={s} term={s}
                                    meaning={SCOPE_WHY[s] ?? "Granted by the network."} />
                          ))}
                        </TipList>
                      ) : (
                        // Older connections predate us storing the granted list.
                        // An empty list would read as "none granted", which is
                        // a different and alarming claim.
                        <TipP>
                          This connection was made before we recorded the granted list.
                          Reconnecting it records what the network reports today.
                        </TipP>
                      )}
                      <TipP muted>
                        Disconnecting revokes our access to this account immediately.
                      </TipP>
                    </InfoTip>
                  </dt>
                  <dd>{c.scopes?.length ? c.scopes.join(", ") : "none recorded"}</dd>
                  <dt>Connected</dt>
                  <dd>{new Date(c.connected_at).toLocaleString()}</dd>
                  {/* Shown to everyone who can see the account, editable only
                      by someone who may organise. A reader has to be able to
                      tell labtest1's Page from sabu's; changing which is which
                      is a different thing from reading it. */}
                  <dt>Brand</dt>
                  <dd>
                    {mayOrganise ? (
                      <select
                        className="brand-picker"
                        value={c.brand_id ?? ""}
                        disabled={busy === `brand:${c.id}`}
                        aria-label={`Brand for ${c.display_name}`}
                        onChange={(e) => {
                          const next = e.target.value || null;
                          setBusy(`brand:${c.id}`);
                          setError(null);
                          setConnectionBrand(c.id, next)
                            .then(reloadAll)
                            .catch((err) => {
                              setError(err.message ?? String(err));
                              void logError("connection_oauth", err, {
                                step: "set_brand", workspaceId,
                                context: { connectionId: c.id, brandId: next },
                              });
                            })
                            .finally(() => setBusy(null));
                        }}
                      >
                        <option value="">Unfiled</option>
                        {brands.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    ) : (
                      brandName(c.brand_id) ?? <span className="muted">Unfiled</span>
                    )}
                  </dd>
                </dl>
              </div>
              {c.status === "expired" && <span className="chip warn">Reconnect needed</span>}
              {c.status === "active" && <span className="chip ok">Active</span>}
              {mayDisconnect && (
                <button className="sm" onClick={() => remove(c)}
                        title={`Revoke Simino Social's access to ${c.display_name}. The stored token is deleted and nothing can be published to this account until you connect it again.`}>
                  Disconnect
                </button>
              )}
            </div>

            {/* "Who can post to this?" belongs on the account, not on a settings
                screen somewhere else: it is the question an owner asks while
                looking at the account, and the answer changes without them. So
                it hangs directly under the card it is about — one collapsed
                link per account, never a stack of identical ones. */}
            <ConnectionAccessPanel
              connectionId={c.id}
              displayName={c.display_name}
              organisations={orgs}
              mayShare={mayDisconnect}
              onChanged={() => void load()}
            />
          </div>
          ))}

      <ConnectionActivity events={activity} />
    </>
  );
}
